import { useAuth } from "react-oidc-context";
import { fetchCurrentUser } from "../resources/user";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import type { CurrentUser } from "@rumble-it/common-lib/types/models/CurrentUser.interface";

export const useCurrentUser = () => {
  const { user } = useAuth();

  type CurrentUserData = CurrentUser & {
    currentUserExpiresAt: number;
    currentUserUpdatedAt: number;
  };

  const currentUserQuery = useQuery<CurrentUserData>({
    enabled: !!user?.profile?.sub,
    queryKey: ["currentUser", user?.profile?.sub],
    queryFn: fetchCurrentUser,
    initialData: () => {
      // If a user sub is defined, get the user data from session storage
      if (user?.profile?.sub) {
        const storedUser = sessionStorage.getItem(
          `api.user:${user?.profile?.sub}`,
        );
        return storedUser ? JSON.parse(storedUser) : undefined;
      }
      // If no user sub is defined, return an empty object
      // This will prevent the query from refetching
      return {};
    },
    initialDataUpdatedAt: () => {
      if (user?.profile?.sub) {
        const storedUser = sessionStorage.getItem(
          `api.user:${user?.profile?.sub}`,
        );
        const initialUser = storedUser ? JSON.parse(storedUser) : undefined;
        return initialUser?.currentUserUpdatedAt * 1000;
      }
      // If no user sub is defined, return an empty object
      // This will prevent the query from refetching
      return 0;
    },
    staleTime: () => {
      if (user?.profile?.sub) {
        const storedUser = sessionStorage.getItem(
          `api.user:${user?.profile?.sub}`,
        );
        const initialUser = storedUser ? JSON.parse(storedUser) : undefined;
        return (
          (initialUser?.currentUserExpiresAt -
            initialUser?.currentUserUpdatedAt) *
          1000
        );
      }
      return 0;
    }, // 5 minutes
  });

  useEffect(() => {
    if (
      !user ||
      !user.profile?.sub ||
      !user.profile?.iat ||
      !user.profile?.exp
    ) {
      return;
    }

    if (
      currentUserQuery?.data &&
      currentUserQuery?.data?.currentUserUpdatedAt !== user?.profile?.iat
    ) {
      sessionStorage.setItem(
        `api.user:${user?.profile?.sub}`,
        JSON.stringify({
          ...currentUserQuery?.data,
          currentUserUpdatedAt: user?.profile?.iat,
          currentUserExpiresAt: user?.profile?.exp,
        }),
      );
    }
  }, [user?.profile?.sub, currentUserQuery?.data]);

  if (Object.keys(currentUserQuery.data || {}).length === 0) {
    return null; // Return null if no user data is available
  }

  return currentUserQuery.data || null;
};
